// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Domain glossary + inline ℹ popovers. `gloss('anomaly','anomaly')` renders a
// clickable term; initGlossary() wires a single fixed popover (click-away /
// Escape to dismiss). Assume the reader knows nothing about climatology.

import { attr, esc } from './format';

export const GLOSSARY: Record<string, { term: string; def: string }> = {
  acorn: {
    term: 'ACORN-SAT',
    def: 'The Australian Climate Observations Reference Network – Surface Air Temperature: the Bureau of Meteorology\'s official, quality-controlled temperature record for 112 long-running stations, homogenised back to 1910. It is the dataset the Bureau uses to report on Australia\'s climate.',
  },
  anomaly: {
    term: 'Temperature anomaly',
    def: 'How much warmer or cooler a year was than a fixed "normal" period. Here the normal is the 1961–1990 average for each station. An anomaly of +1 °C means that year was 1 °C above its own 1961–1990 average. Anomalies let you compare a cool coastal town with a hot inland one on the same scale.',
  },
  baseline: {
    term: '1961–1990 baseline',
    def: 'The reference period against which anomalies are measured — a World Meteorological Organization standard "normal". Every anomaly on this site is relative to each station\'s own 1961–1990 average.',
  },
  homogenised: {
    term: 'Homogenisation',
    def: 'Adjusting a station\'s raw readings to remove artificial jumps caused by the site moving, the thermometer changing, or a screen being replaced — so the record reflects the climate, not the equipment. It is why a homogenised record can differ slightly from raw station numbers.',
  },
  rate: {
    term: 'Warming rate (°C/decade)',
    def: 'The slope of a straight line fitted through a station\'s annual temperatures — how much it warms per decade on average. A rate of 0.13 °C/decade is about 1.3 °C per century.',
  },
  tmax: {
    term: 'Maximum temperature',
    def: 'The hottest point of the day — the daytime high. Averaged over a year it gives the mean daytime temperature.',
  },
  tmin: {
    term: 'Minimum temperature',
    def: 'The coldest point of the day, almost always overnight. Averaged over a year it gives the mean night-time temperature.',
  },
  mean: {
    term: 'Mean temperature',
    def: 'The average of the daily maximum and minimum — a simple measure of overall warmth used for the headline warming figures.',
  },
  hotday: {
    term: 'Hot day (35 °C+)',
    def: 'A day whose maximum temperature reached at least 35 °C. Counting them per year shows how heat extremes are changing, which matters for health, fire risk and agriculture.',
  },
  frost: {
    term: 'Frost night (≤ 2 °C)',
    def: 'A night whose minimum fell to 2 °C or below — cold enough for frost to form near the ground. Fewer frost nights lengthen growing seasons but can also disrupt crops that need winter chill.',
  },
  trend: {
    term: 'Trend line',
    def: 'A straight line fitted through the yearly values by least squares. Its slope summarises the long-term direction; individual years still wobble above and below it.',
  },
  station: {
    term: 'Reference station',
    def: 'One of 112 weather stations chosen for their long, high-quality records and spread across the country. Some closed and were merged with a nearby site; the record follows the location, not a single instrument.',
  },
};

export function gloss(key: keyof typeof GLOSSARY | string, label?: string): string {
  const g = GLOSSARY[key as string];
  if (!g) return esc(label ?? String(key));
  return `<span class="glossary-link" data-term="${attr(String(key))}" role="button" tabindex="0" aria-label="${attr(g.term)} — definition">${esc(label ?? g.term)}<span class="gl-i" aria-hidden="true">ℹ</span></span>`;
}

let popEl: HTMLDivElement | null = null;

function ensurePop(): HTMLDivElement {
  if (!popEl) {
    popEl = document.createElement('div');
    popEl.className = 'gloss-pop';
    popEl.setAttribute('role', 'dialog');
    document.body.appendChild(popEl);
  }
  return popEl;
}

function show(target: HTMLElement, key: string): void {
  const g = GLOSSARY[key];
  if (!g) return;
  const el = ensurePop();
  el.innerHTML = `<strong>${esc(g.term)}</strong><p>${esc(g.def)}</p>`;
  el.classList.add('visible');
  const r = target.getBoundingClientRect();
  const pw = Math.min(320, window.innerWidth - 24);
  el.style.width = `${pw}px`;
  let left = r.left;
  if (left + pw + 12 > window.innerWidth) left = window.innerWidth - pw - 12;
  el.style.left = `${Math.max(12, left)}px`;
  const below = r.bottom + 8;
  const hh = el.offsetHeight;
  el.style.top = below + hh + 12 > window.innerHeight && r.top - hh - 8 > 8 ? `${r.top - hh - 8}px` : `${below}px`;
}

export function hideGloss(): void { if (popEl) popEl.classList.remove('visible'); }

export function initGlossary(): void {
  document.addEventListener('click', (e) => {
    const link = (e.target as Element).closest('.glossary-link') as HTMLElement | null;
    if (link) {
      e.stopPropagation();
      show(link, link.getAttribute('data-term') || '');
      return;
    }
    if (popEl && !(e.target as Element).closest('.gloss-pop')) hideGloss();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') hideGloss();
    if ((e.key === 'Enter' || e.key === ' ') && (e.target as Element).classList?.contains('glossary-link')) {
      e.preventDefault();
      show(e.target as HTMLElement, (e.target as HTMLElement).getAttribute('data-term') || '');
    }
  });
  window.addEventListener('scroll', hideGloss, true);
}
